import { makeStyles } from "tss-react/mui";
import type { CollisionDetection } from "@dnd-kit/core";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  pointerWithin,
} from "@dnd-kit/core";
import { Box } from "@mui/material";
import { AutoAssignWindowIndicators } from "./components/AutoAssignWindowIndicators";
import { CurrentTimeIndicator } from "./components/CurrentTimeIndicator";
import { PrioritySchedulerBuckets } from "./components/PrioritySchedulerBuckets";
import { PrioritySchedulerColumn } from "./components/PrioritySchedulerColumn";
import { PrioritySchedulerColumnHeader } from "./components/PrioritySchedulerColumnHeader";
import { AutoAssignWindows } from "../MiScheduler/TimeGutter/AutoAssignWindows";
import { TimeGutter } from "../MiScheduler/TimeGutter/TimeGutter";
import { MiWorkOrderCard } from "../MiWorkOrderCard/MiWorkOrderCard";
import { IPrioritySchedulerProps } from "./PrioritySchedulerBoard.types";
import { usePrioritySchedulerBoardViewModel } from "./usePrioritySchedulerBoardViewModel";

const collisionDetectionStrategy: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }
  return closestCenter(args);
};

export function PrioritySchedulerBoard({
  columns = [],
  columnWidth,
  hourHeight,
  date,
  zoom = 1,
  calendarSet,
  tenantHolidays,
  onEventRemove,
  trays,
  headerActions = [],
  onHeaderActionClick,
  onCardClick = (_) => null,
  EventComponent = MiWorkOrderCard,
  onCardMove,
}: Readonly<IPrioritySchedulerProps>) {
  const viewModel = usePrioritySchedulerBoardViewModel({
    tenantHolidays,
    columns,
    columnWidth,
    hourHeight,
    date,
    zoom,
    calendarSet,
    trays,
    onCardMove,
  });
  const { dayStartTopPosition } = viewModel;
  const { classes, cx } = useStyles({ dayStartTopPosition });

  return (
    <Box className={classes.prioritySchedulerContainer}>
      <DndContext
        sensors={viewModel.sensors}
        collisionDetection={collisionDetectionStrategy}
        onDragStart={viewModel.handleDragStart}
        onDragMove={viewModel.handleDragMove}
        onDragOver={viewModel.handleDragOver}
        onDragCancel={viewModel.handleDragCancel}
        onDragEnd={viewModel.handleDragEnd}
      >
        <Box className={classes.priorityScheduler}>
          <Box
            ref={viewModel.schedulerColumnsRef}
            className={classes.prioritySchedulerColumns}
            height={viewModel.totalContentHeight}
          >
            <Box className={classes.currentTimeIndicatorContainer}>
              <Box className={classes.currentTimeIndicatorContainerInner}>
                <Box className={classes.stickyHeader} />
                <Box
                  className={classes.currentTimeIndicatorWrapper}
                  sx={{ width: viewModel.indicatorWidth ?? 0 }}
                >
                  {viewModel.workTemplateBases.length > 0 && (
                    <AutoAssignWindowIndicators
                      workTemplateBases={viewModel.workTemplateBases}
                      hourHeight={viewModel.calculatedHourHeight}
                    />
                  )}
                  <CurrentTimeIndicator
                    hourHeight={viewModel.calculatedHourHeight}
                  />
                </Box>
              </Box>
            </Box>
            <Box
              className={classes.stickyGutter}
              display="flex"
              alignItems="center"
              height="100%"
            >
              <TimeGutter
                intervals={viewModel.intervals}
                adjustedHeight={viewModel.calculatedHourHeight}
              />
              {viewModel.workTemplateBases.length > 0 && (
                <AutoAssignWindows
                  workTemplateBases={viewModel.workTemplateBases}
                  intervals={viewModel.intervals}
                  adjustedHeight={viewModel.calculatedHourHeight}
                />
              )}
            </Box>

            <Box
              ref={viewModel.dayStartMarkerRef}
              className={classes.dayStartMarker}
              data-testid="day-start-marker"
            />
            {viewModel.columnsWithMeta.map(
              ({
                column,
                columnDate,
                holdCount,
                eventCount,
                columnDnDId,
                holiday,
              }) => {
                const dragIndicatorY =
                  viewModel.activeCard &&
                  viewModel.dragIndicatorState?.columnId === column.id
                    ? viewModel.dragIndicatorState.offsetY
                    : undefined;

                return (
                  <Box
                    key={column?.id}
                    width={columnWidth}
                    flex="0 0 auto"
                    className={classes.columnWrapper}
                  >
                    <Box
                      className={cx(
                        classes.stickyHeader,
                        classes.stickyHeaderBlue,
                        holiday && classes.stickyHeaderGrey
                      )}
                    >
                      <PrioritySchedulerColumnHeader
                        {...column}
                        date={columnDate}
                        user={column.user}
                        actions={headerActions}
                        holdCount={holdCount}
                        eventCount={eventCount}
                        dateView={column?.dateView}
                        onActionClick={onHeaderActionClick}
                      />
                    </Box>
                    <PrioritySchedulerColumn
                      {...column}
                      columnDnDId={columnDnDId}
                      holiday={holiday}
                      date={columnDate}
                      EventComponent={EventComponent}
                      hourHeight={viewModel.calculatedHourHeight}
                      dayStartTime={viewModel.dayStartTime}
                      dayEndTime={viewModel.dayEndTime}
                      timelineContentHeight={viewModel.totalContentHeight}
                      layoutReferenceDate={date}
                      dragIndicatorY={dragIndicatorY}
                      workTemplateBases={viewModel.workTemplateBases}
                      registerColumnDroppableNode={
                        viewModel.registerColumnDroppableNode
                      }
                      onCardClick={onCardClick}
                      onEventRemove={onEventRemove}
                    />
                  </Box>
                );
              }
            )}
          </Box>
        </Box>
        <PrioritySchedulerBuckets
          trays={trays}
          onCardClick={onCardClick}
          EventComponent={EventComponent}
          indicatorWidth={viewModel.indicatorWidth ?? 0}
        />
        <DragOverlay>
          {viewModel.activeCard ? (
            <Box width={60} sx={{ opacity: 0.95 }}>
              <EventComponent
                attempt={viewModel.activeCard}
                onCardClick={onCardClick}
              />
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>
    </Box>
  );
}

const useStyles = makeStyles<{ dayStartTopPosition: number }>()(
  (theme, { dayStartTopPosition }) => ({
    prioritySchedulerContainer: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
    },
    priorityScheduler: {
      display: "flex",
      paddingTop: theme.spacing(1),
      minWidth: 0,
    },
    prioritySchedulerColumns: {
      display: "flex",
      backgroundColor: theme.palette.common.white,
      gap: theme.spacing(1),
      position: "relative",
      minWidth: 0,
      width: "max-content",
    },
    currentTimeIndicatorWrapper: {
      height: "100%",
      width: "100%",
      position: "relative",
    },
    currentTimeIndicatorContainer: {
      width: "100%",
      position: "absolute",
      zIndex: 3,
      height: "100%",
      top: 0,
      left: 0,
      right: 0,
      pointerEvents: "none",
    },
    currentTimeIndicatorContainerInner: {
      position: "relative",
      width: "100%",
      height: "100%",
    },
    stickyGutter: {
      position: "sticky",
      display: "flex",
      left: 0,
      zIndex: 2,
      marginTop: 38,
    },
    stickyHeader: {
      position: "sticky",
      top: 0,
      padding: theme.spacing(0, 1),
      height: 38,
      zIndex: 3,
      backgroundColor: theme.palette.common.white,
    },
    stickyHeaderBlue: {
      backgroundColor: theme.palette.blue.light,
      padding: 4,
      borderTopRightRadius: theme.spacing(1),
      borderTopLeftRadius: theme.spacing(1),
    },
    stickyHeaderGrey: {
      backgroundColor: theme.palette.grey.lightAccent,
    },
    columnWrapper: {
      display: "flex",
      flexDirection: "column",
      alignSelf: "stretch",
      minHeight: 0,
      height: "100%",
      border: "2px solid transparent",
      borderRadius: theme.spacing(1),
      transition: "border-color 0.15s ease-in-out",
    },
    dropTargetColumnWrapper: {
      borderColor: theme.palette.primary.main,
    },
    dayStartMarker: {
      position: "absolute",
      left: 0,
      top: dayStartTopPosition,
      width: 1,
      height: 1,
      pointerEvents: "none",
    },
  })
);
